import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useRef } from 'react';
import { useResolvedOnServer } from '~/context/common/DeferredResolutionContext';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import DiscountContext from '~/context/vdp/DiscountContext';
import useSkipFirstFetchIfResolvedOnServer from '~/hooks/common/useSkipFirstFetchIfResolvedOnServer';
import fetchDiscounts from '~/services/vdp/fetchDiscounts';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetDiscounts(isLoggedIn: boolean) {
  const {
    discounts: { setData, setError, setLoading },
    fetchDiscountsFlag
  } = useContext(DiscountContext);
  const { discounts: discountsResolvedOnServer } = useResolvedOnServer();
  const shouldSkipFetch = useSkipFirstFetchIfResolvedOnServer(
    discountsResolvedOnServer
  );
  const lastFetchFlag = useRef(fetchDiscountsFlag);
  const eventCorrelationId = useEventCorrelationId();

  useEffectWithController(
    controller => {
      if (!isLoggedIn) {
        setData([]);

        return;
      }

      const wasTriggered = fetchDiscountsFlag !== lastFetchFlag.current;
      lastFetchFlag.current = fetchDiscountsFlag;

      // A miss-triggered fetch always runs, even during the server-resolved skip window.
      if (shouldSkipFetch() && !wasTriggered) {
        return;
      }

      setLoading();

      const discounts = fetchDiscounts(eventCorrelationId, controller.signal);

      discounts
        .then(setData)
        .catch(
          generateErrorLogger(
            'Get discounts',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      eventCorrelationId,
      fetchDiscountsFlag,
      isLoggedIn,
      setData,
      setError,
      setLoading,
      shouldSkipFetch
    ]
  );
}
